import { useCallback, useEffect, useMemo, useState } from "react";
import { ethers } from "ethers";
import { useWallet } from "../context/WalletContext.jsx";
import { getContract } from "../services/contracts.js";
import { getTxOverrides } from "../services/gas.js";
import { decodeContractError } from "../utils/contractError.js";
import config from "../config/index.js";
import ResultModal from "../components/ResultModal.jsx";

/**
 * $GILD Marketplace — a Uniswap-V3-backed swap widget for buying and
 * selling $GILD (GIL) against USDC.
 *
 * The official `@uniswap/widgets` package can't drive this pool (it targets
 * ethers v5 / React 17 and only routes against Uniswap's hosted token lists,
 * which don't know our GIL/USDC pair), so this is a purpose-built widget that
 * talks straight to the SAME Uniswap V3 SwapRouter the protocol's 10%
 * token-buy leg uses (`GilderTokenBuyRouter.swapRouter`). Buy = USDC→GIL,
 * Sell = GIL→USDC, both via `exactInputSingle` with a slippage-protected
 * `amountOutMinimum`. Quotes are computed from the full-range pool's reserves
 * (constant-product, 0.30% fee) — the same math the fill uses.
 *
 * The router address is discovered on-chain from `tokenBuyRouter.swapRouter()`
 * unless `VITE_UNISWAP_ROUTER_ADDRESS` overrides it.
 */

const USDC_DECIMALS = 6;
const GIL_DECIMALS = 18;
const BPS = 10_000n;
const DEADLINE_SECS = 1200; // 20 min
const QUOTE_DEBOUNCE_MS = 350;

// 0.30% GIL/USDC Uniswap V3 pool.
const POOL_FEE = 3000;

// Minimal human-readable ABIs — ethers v6 parses these directly.
// SwapRouter02 signature — ExactInputSingleParams has NO `deadline` field.
const ROUTER_ABI = [
    "function exactInputSingle((address tokenIn,address tokenOut,uint24 fee,address recipient,uint256 amountIn,uint256 amountOutMinimum,uint160 sqrtPriceLimitX96)) payable returns (uint256 amountOut)",
    "function factory() view returns (address)"
];
const FACTORY_ABI = ["function getPool(address,address,uint24) view returns (address)"];
const ERC20_BALANCE_ABI = ["function balanceOf(address) view returns (uint256)"];
const TOKEN_BUY_ROUTER_ABI = ["function swapRouter() view returns (address)"];

// Constant-product output with the pool's 0.30% fee (997/1000). A full-range
// V3 position's token balances behave as reserves, so this matches the on-chain
// fill and the protocol contracts' own quote math.
function getAmountOut(amountIn, reserveIn, reserveOut) {
    if (reserveIn <= 0n || reserveOut <= 0n || amountIn <= 0n) return 0n;
    const inWithFee = amountIn * 997n;
    return (inWithFee * reserveOut) / (reserveIn * 1000n + inWithFee);
}

// Reads the V3 pool's live (GIL, USDC) reserves via the SwapRouter's factory.
async function readReserves(routerAddress, provider, gilAddr, usdcAddr) {
    const router = new ethers.Contract(routerAddress, ROUTER_ABI, provider);
    const factory = new ethers.Contract(await router.factory(), FACTORY_ABI, provider);
    const pool = await factory.getPool(gilAddr, usdcAddr, POOL_FEE);
    if (!pool || pool === ethers.ZeroAddress) return null;
    const gil = new ethers.Contract(gilAddr, ERC20_BALANCE_ABI, provider);
    const usdc = new ethers.Contract(usdcAddr, ERC20_BALANCE_ABI, provider);
    const [rGil, rUsdc] = await Promise.all([gil.balanceOf(pool), usdc.balanceOf(pool)]);
    return { gil: BigInt(rGil), usdc: BigInt(rUsdc) };
}

const SLIPPAGE_PRESETS = [
    { label: "0.5%", bps: 50 },
    { label: "1%", bps: 100 },
    { label: "3%", bps: 300 }
];

// A read-only provider so quotes + router discovery work even before the
// user connects a wallet. Falls back gracefully if the RPC is unset.
let cachedReadProvider = null;
function getReadProvider() {
    if (cachedReadProvider) return cachedReadProvider;
    try {
        cachedReadProvider = new ethers.JsonRpcProvider(config.network.rpcUrl);
    } catch {
        cachedReadProvider = null;
    }
    return cachedReadProvider;
}

export default function Marketplace() {
    const { address, signer, wrongChain, switchNetwork, requestConnect } = useWallet();

    // "buy" = spend USDC to get GIL; "sell" = spend GIL to get USDC.
    const [side, setSide] = useState("buy");
    const [amount, setAmount] = useState("");
    const [slippageBps, setSlippageBps] = useState(100);

    const [routerAddress, setRouterAddress] = useState(config.contracts.uniswapRouter || null);
    const [routerError, setRouterError] = useState(null);

    const [quote, setQuote] = useState(null); // { out: bigint } estimated output
    const [quoting, setQuoting] = useState(false);
    const [quoteError, setQuoteError] = useState(null);

    const [usdcBalance, setUsdcBalance] = useState(null); // bigint | null
    const [gilBalance, setGilBalance] = useState(null);

    const [busy, setBusy] = useState(false);
    const [statusText, setStatusText] = useState("");
    const [result, setResult] = useState(null);

    const gilAddr = config.contracts.gil;
    const usdcAddr = config.contracts.usdc;

    // Token metadata for the current side.
    const inMeta = side === "buy"
        ? { symbol: "USDC", decimals: USDC_DECIMALS, address: usdcAddr, balance: usdcBalance }
        : { symbol: "$GILD", decimals: GIL_DECIMALS, address: gilAddr, balance: gilBalance };
    const outMeta = side === "buy"
        ? { symbol: "$GILD", decimals: GIL_DECIMALS, address: gilAddr, balance: gilBalance }
        : { symbol: "USDC", decimals: USDC_DECIMALS, address: usdcAddr, balance: usdcBalance };

    // Parse the typed amount into wei; null when empty/invalid.
    const amountInWei = useMemo(() => {
        if (!amount || Number(amount) <= 0) return null;
        try {
            return ethers.parseUnits(String(amount), inMeta.decimals);
        } catch {
            return null;
        }
    }, [amount, inMeta.decimals]);

    // ── Router discovery ────────────────────────────────────────────────
    useEffect(() => {
        if (routerAddress) return; // already have it (config override or resolved)
        let cancelled = false;
        (async () => {
            const readProvider = getReadProvider();
            if (!readProvider) return;
            const tbrAddr = config.contracts.tokenBuyRouter;
            if (!tbrAddr) {
                setRouterError(
                    "Swap router is not configured. Set VITE_UNISWAP_ROUTER_ADDRESS or VITE_TOKEN_BUY_ROUTER_ADDRESS."
                );
                return;
            }
            try {
                const tbr = new ethers.Contract(tbrAddr, TOKEN_BUY_ROUTER_ABI, readProvider);
                const resolved = await tbr.swapRouter();
                if (!cancelled && resolved && resolved !== ethers.ZeroAddress) {
                    setRouterAddress(resolved);
                }
            } catch (err) {
                if (!cancelled) {
                    setRouterError(
                        "Couldn't resolve the swap router from the token-buy router. " +
                        (err?.shortMessage || err?.message || "")
                    );
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [routerAddress]);

    // ── Balances ────────────────────────────────────────────────────────
    const refreshBalances = useCallback(async () => {
        if (!address) {
            setUsdcBalance(null);
            setGilBalance(null);
            return;
        }
        const runner = signer || getReadProvider();
        if (!runner) return;
        try {
            const usdc = getContract("usdc", runner);
            const gil = getContract("gil", runner);
            const [u, g] = await Promise.all([usdc.balanceOf(address), gil.balanceOf(address)]);
            setUsdcBalance(BigInt(u));
            setGilBalance(BigInt(g));
        } catch (err) {
            console.warn("Marketplace balance fetch failed:", err?.message || err);
        }
    }, [address, signer]);

    useEffect(() => {
        refreshBalances();
    }, [refreshBalances]);

    // ── Live quote (debounced) ──────────────────────────────────────────
    useEffect(() => {
        setQuoteError(null);
        if (!routerAddress || !amountInWei) {
            setQuote(null);
            setQuoting(false);
            return undefined;
        }
        let cancelled = false;
        setQuoting(true);
        const handle = setTimeout(async () => {
            const readProvider = signer?.provider || getReadProvider();
            if (!readProvider) {
                setQuoting(false);
                return;
            }
            try {
                const reserves = await readReserves(routerAddress, readProvider, gilAddr, usdcAddr);
                if (cancelled) return;
                if (!reserves) {
                    setQuote(null);
                    setQuoteError("Pool not found for this pair.");
                    return;
                }
                const reserveIn = side === "buy" ? reserves.usdc : reserves.gil;
                const reserveOut = side === "buy" ? reserves.gil : reserves.usdc;
                const out = getAmountOut(amountInWei, reserveIn, reserveOut);
                if (out <= 0n) {
                    setQuote(null);
                    setQuoteError("Not enough pool liquidity for this amount.");
                } else {
                    setQuote({ out });
                    setQuoteError(null);
                }
            } catch (err) {
                if (cancelled) return;
                setQuote(null);
                setQuoteError(friendlyQuoteError(err));
            } finally {
                if (!cancelled) setQuoting(false);
            }
        }, QUOTE_DEBOUNCE_MS);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [routerAddress, amountInWei, side, gilAddr, usdcAddr, signer]);

    const minReceived = useMemo(() => {
        if (!quote) return null;
        return (quote.out * (BPS - BigInt(slippageBps))) / BPS;
    }, [quote, slippageBps]);

    // Implied price: USDC per 1 $GILD, derived from the current quote.
    const impliedPrice = useMemo(() => {
        if (!quote || !amountInWei) return null;
        const inAmt = Number(ethers.formatUnits(amountInWei, inMeta.decimals));
        const outAmt = Number(ethers.formatUnits(quote.out, outMeta.decimals));
        if (inAmt <= 0 || outAmt <= 0) return null;
        // buy: spend USDC(in) for GIL(out) → price = usdcIn / gilOut
        // sell: spend GIL(in) for USDC(out) → price = usdcOut / gilIn
        return side === "buy" ? inAmt / outAmt : outAmt / inAmt;
    }, [quote, amountInWei, inMeta.decimals, outMeta.decimals, side]);

    const insufficient =
        amountInWei != null && inMeta.balance != null && amountInWei > inMeta.balance;

    // ── Swap execution ──────────────────────────────────────────────────
    async function handleSwap() {
        if (!signer || !routerAddress || !amountInWei) return;
        setBusy(true);
        setResult(null);
        try {
            const overrides = await getTxOverrides(signer.provider);
            const router = new ethers.Contract(routerAddress, ROUTER_ABI, signer);
            const tokenIn = getContract(side === "buy" ? "usdc" : "gil", signer);

            // 1. Approve the router to pull `tokenIn` if the allowance is short.
            setStatusText(`Checking ${inMeta.symbol} approval…`);
            let allowance = 0n;
            try {
                allowance = BigInt(await tokenIn.allowance(address, routerAddress));
            } catch {
                allowance = 0n;
            }
            if (allowance < amountInWei) {
                setStatusText(`Approving ${inMeta.symbol}…`);
                const approveTx = await tokenIn.approve(routerAddress, ethers.MaxUint256, overrides);
                await approveTx.wait();
            }

            // 2. Re-quote from the freshest reserves, then apply the slippage floor.
            setStatusText("Fetching best price…");
            const reserves = await readReserves(routerAddress, signer.provider || getReadProvider(), gilAddr, usdcAddr);
            const reserveIn = side === "buy" ? reserves.usdc : reserves.gil;
            const reserveOut = side === "buy" ? reserves.gil : reserves.usdc;
            const freshOut = getAmountOut(amountInWei, reserveIn, reserveOut);
            const amountOutMin = (freshOut * (BPS - BigInt(slippageBps))) / BPS;

            // 3. Swap via V3 (SwapRouter02) exactInputSingle.
            setStatusText(side === "buy" ? "Buying $GILD…" : "Selling $GILD…");
            const swapTx = await router.exactInputSingle(
                {
                    tokenIn: inMeta.address,
                    tokenOut: outMeta.address,
                    fee: POOL_FEE,
                    recipient: address,
                    amountIn: amountInWei,
                    amountOutMinimum: amountOutMin,
                    sqrtPriceLimitX96: 0n
                },
                overrides
            );
            const receipt = await swapTx.wait();

            const outHuman = trimAmount(ethers.formatUnits(freshOut, outMeta.decimals));
            const inHuman = trimAmount(ethers.formatUnits(amountInWei, inMeta.decimals));
            setResult({
                type: "success",
                title: side === "buy" ? "$GILD Purchased" : "$GILD Sold",
                message:
                    `Swapped ${inHuman} ${inMeta.symbol} for ~${outHuman} ${outMeta.symbol}. ` +
                    `Tx ${receipt?.hash ? shortHash(receipt.hash) : "confirmed"}.`
            });
            setAmount("");
            setQuote(null);
            refreshBalances();
        } catch (err) {
            setResult({
                type: "error",
                title: "Swap Failed",
                message: decodeContractError(err)
            });
        } finally {
            setBusy(false);
            setStatusText("");
        }
    }

    function flipSide() {
        setSide((s) => (s === "buy" ? "sell" : "buy"));
        setAmount("");
        setQuote(null);
        setQuoteError(null);
    }

    function setMax() {
        if (inMeta.balance == null) return;
        setAmount(ethers.formatUnits(inMeta.balance, inMeta.decimals));
    }

    const notConfigured = !gilAddr || !usdcAddr;

    // ── Primary action button state ─────────────────────────────────────
    let actionLabel = side === "buy" ? "Buy $GILD" : "Sell $GILD";
    let actionDisabled = false;
    let actionOnClick = handleSwap;
    if (!address) {
        actionLabel = "Connect Wallet";
        actionOnClick = () => requestConnect("/marketplace");
    } else if (wrongChain) {
        actionLabel = `Switch to ${config.network.name}`;
        actionOnClick = switchNetwork;
    } else if (notConfigured || routerError) {
        actionLabel = "Unavailable";
        actionDisabled = true;
    } else if (busy) {
        actionLabel = statusText || "Working…";
        actionDisabled = true;
    } else if (!amountInWei) {
        actionLabel = "Enter an amount";
        actionDisabled = true;
    } else if (insufficient) {
        actionLabel = `Insufficient ${inMeta.symbol}`;
        actionDisabled = true;
    } else if (quoteError) {
        actionLabel = "No route / liquidity";
        actionDisabled = true;
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="mx-auto w-full max-w-[460px]">
                <div className="rounded-[16px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 shadow-xl">
                    {/* Buy / Sell toggle */}
                    <div className="grid grid-cols-2 gap-1 rounded-lg bg-[#0E2C19] p-1">
                        {["buy", "sell"].map((s) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => {
                                    if (s !== side) flipSide();
                                }}
                                className={`rounded-md py-1.5 text-[12px] font-semibold uppercase tracking-widest transition-colors ${
                                    side === s
                                        ? "bg-[#FBC206] text-[#10281b]"
                                        : "text-[#e6c985]/70 hover:text-[#FBC206]"
                                }`}
                            >
                                {s === "buy" ? "Buy" : "Sell"}
                            </button>
                        ))}
                    </div>

                    {/* Pay field */}
                    <TokenField
                        label="You pay"
                        symbol={inMeta.symbol}
                        value={amount}
                        onChange={setAmount}
                        balance={inMeta.balance}
                        decimals={inMeta.decimals}
                        onMax={setMax}
                        editable
                        error={insufficient ? `Not enough ${inMeta.symbol}` : null}
                    />

                    {/* Flip */}
                    <div className="relative my-1 flex justify-center">
                        <button
                            type="button"
                            onClick={flipSide}
                            aria-label="Switch buy/sell direction"
                            className="absolute -top-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#0E2C19] ring-1 ring-[#bfb67b] text-[#FBC206] hover:-translate-y-0.5 transition-transform"
                        >
                            <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="6 9 10 5 14 9" />
                                <polyline points="6 11 10 15 14 11" />
                            </svg>
                        </button>
                    </div>

                    {/* Receive field */}
                    <TokenField
                        label="You receive (estimated)"
                        symbol={outMeta.symbol}
                        value={
                            quoting
                                ? "…"
                                : quote
                                    ? trimAmount(ethers.formatUnits(quote.out, outMeta.decimals))
                                    : ""
                        }
                        balance={outMeta.balance}
                        decimals={outMeta.decimals}
                        editable={false}
                    />

                    {/* Slippage */}
                    <div className="mt-3 flex items-center justify-between">
                        <span className="text-[11px] font-medium text-[#e6c985]/70">Max slippage</span>
                        <div className="inline-flex gap-1">
                            {SLIPPAGE_PRESETS.map((p) => (
                                <button
                                    key={p.bps}
                                    type="button"
                                    onClick={() => setSlippageBps(p.bps)}
                                    className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ring-1 transition-colors ${
                                        slippageBps === p.bps
                                            ? "bg-[#FBC206] text-[#10281b] ring-[#FBC206]"
                                            : "text-[#e6c985]/70 ring-white/15 hover:ring-[#bfb67b]"
                                    }`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Quote details */}
                    <div className="mt-3 space-y-1.5 rounded-lg bg-[#0E2C19] px-3 py-2 text-[11px]">
                        <DetailRow
                            label="Rate"
                            value={impliedPrice != null ? `1 $GILD ≈ ${formatSmallUsd(impliedPrice)}` : "—"}
                        />
                        <DetailRow
                            label="Minimum received"
                            value={
                                minReceived != null
                                    ? `${trimAmount(ethers.formatUnits(minReceived, outMeta.decimals))} ${outMeta.symbol}`
                                    : "—"
                            }
                        />
                        <DetailRow label="Slippage tolerance" value={`${(slippageBps / 100).toFixed(2)}%`} />
                    </div>

                    {quoteError ? (
                        <p className="mt-2 text-[11px] font-medium text-[#C93030]">{quoteError}</p>
                    ) : null}
                    {routerError ? (
                        <p className="mt-2 text-[11px] font-medium text-[#C93030]">{routerError}</p>
                    ) : null}
                    {notConfigured ? (
                        <p className="mt-2 text-[11px] font-medium text-[#C93030]">
                            GIL/USDC token addresses are not configured for this environment.
                        </p>
                    ) : null}

                    <button
                        type="button"
                        onClick={actionOnClick}
                        disabled={actionDisabled}
                        className={`mt-4 w-full rounded-lg px-4 py-3 text-[13px] font-bold transition-all ${
                            actionDisabled
                                ? "cursor-not-allowed bg-white/10 text-white/45"
                                : "bg-[#FBC206] text-[#10281b] hover:-translate-y-0.5 hover:shadow-lg"
                        }`}
                    >
                        {actionLabel}
                    </button>

                    <p className="mt-3 text-center text-[10px] leading-snug text-[#e6c985]/50">
                        Swaps route through Uniswap V3. You&apos;ll approve {inMeta.symbol} once,
                        then confirm the swap in your wallet.
                    </p>
                </div>
            </div>

            <ResultModal
                open={Boolean(result)}
                type={result?.type}
                title={result?.title || ""}
                message={result?.message || ""}
                primaryAction={
                    result?.type === "success"
                        ? { label: "View My Tokens", to: "/my-tokens" }
                        : undefined
                }
                onClose={() => setResult(null)}
            />
        </div>
    );
}

function TokenField({ label, symbol, value, onChange, balance, decimals, onMax, editable, error }) {
    return (
        <div className={`mt-2 rounded-lg bg-[#0E2C19] px-3 py-2.5 ring-1 ${error ? "ring-[#C93030]/60" : "ring-white/10"}`}>
            <div className="flex items-center justify-between">
                <span className="text-[10px] font-medium uppercase tracking-widest text-[#e6c985]/60">
                    {label}
                </span>
                {balance != null ? (
                    <span className="text-[10px] text-[#e6c985]/60">
                        Balance: {trimAmount(ethers.formatUnits(balance, decimals))}
                        {onMax ? (
                            <button
                                type="button"
                                onClick={onMax}
                                className="ml-1.5 font-semibold text-[#FBC206] hover:underline"
                            >
                                MAX
                            </button>
                        ) : null}
                    </span>
                ) : null}
            </div>
            <div className="mt-1 flex items-center gap-2">
                {editable ? (
                    <input
                        inputMode="decimal"
                        placeholder="0.0"
                        value={value}
                        onChange={(e) => {
                            const v = e.target.value;
                            // Allow only a valid partial decimal number.
                            if (v === "" || /^\d*\.?\d*$/.test(v)) onChange(v);
                        }}
                        className="w-full bg-transparent text-[22px] font-semibold text-[#e6c985] placeholder-[#e6c985]/30 outline-none"
                    />
                ) : (
                    <div className="w-full truncate text-[22px] font-semibold text-[#e6c985]">
                        {value || <span className="text-[#e6c985]/30">0.0</span>}
                    </div>
                )}
                <span className="shrink-0 rounded-full bg-[#10281b] px-2.5 py-1 text-[12px] font-bold text-[#FBC206] ring-1 ring-[#bfb67b]">
                    {symbol}
                </span>
            </div>
        </div>
    );
}

function DetailRow({ label, value }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-[#e6c985]/60">{label}</span>
            <span className="font-semibold text-[#e6c985]">{value}</span>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

// Trims a decimal string to at most 6 significant fractional digits without
// scientific notation, dropping trailing zeros.
function trimAmount(str) {
    if (str == null) return "";
    const n = Number(str);
    if (!Number.isFinite(n)) return String(str);
    if (n === 0) return "0";
    if (n >= 1) return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
    // Sub-1: keep enough decimals for ~4 significant figures.
    const decimals = Math.min(18, Math.max(4, 3 - Math.floor(Math.log10(Math.abs(n)))));
    return n.toFixed(decimals).replace(/\.?0+$/, "");
}

// USD price formatter that never emits scientific notation — $GILD trades at
// micro-cap prices against a 1e12 supply.
function formatSmallUsd(value) {
    if (value == null || !Number.isFinite(value) || value <= 0) return "—";
    if (value >= 1) return `$${value.toFixed(4)}`;
    const decimals = Math.min(20, Math.max(4, 2 - Math.floor(Math.log10(value))));
    return `$${value.toFixed(decimals)}`;
}

function friendlyQuoteError(err) {
    const msg = String(err?.shortMessage || err?.message || "");
    if (/INSUFFICIENT_LIQUIDITY|INSUFFICIENT_INPUT/i.test(msg)) {
        return "Not enough pool liquidity for this amount.";
    }
    return "Couldn't fetch a price for this amount.";
}

function shortHash(hash) {
    return `${hash.slice(0, 6)}…${hash.slice(-4)}`;
}
